import React, { useState, useEffect, useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { AuthProvider, AuthContext } from "./context/AuthContext";
import { SnackbarProvider } from "notistack";
import { ErrorBoundary } from "react-error-boundary";
import { setErrorDisplayCallback, sendErrorEmail } from "./services/api";
import ErrorModal from "./components/ErrorModal";

import Sidebar from "./components/Sidebar";
import Login from "./components/Login";
import Register from "./components/Register";
import ForgotPassword from "./components/ForgotPassword";
import ForgotPasswordVerify from "./components/ForgotPasswordVerify";
import ResetPassword from "./components/ResetPassword";
import UretimDonemiPage from "./pages/UretimDonemiPage";
import UreticiBilgileri from "./pages/UreticiBilgileri";
import KumeBilgileri from "./pages/KumeBilgileri";
import PerformansRaporlari from "./pages/PerformansRaporlari";
import MuhasebeRaporlari from "./pages/MuhasebeRaporlari";
import SistemUyarilari from "./pages/SistemUyarilari";
import SSS from "./pages/SSS";
import HesapBilgilerim from "./pages/HesapBilgilerim";
import DestekTalepleri from "./pages/DestekTalepleri";
import KullanimKilavuzu from "./pages/KullanimKilavuzu";
import LisansKosullari from "./pages/LisansKosullari";
import VeterinerlikHizmetleri from "./components/VeterinerlikHizmetleri";
import BakicilarimPage from "./pages/BakicilarimPage";
import DonemDetayPage from "./pages/DonemDetayPage";
import KumesKartiPage from "./pages/KumesKartiPage";

// GlobalErrorFallback component'iniz aynı kalıyor
const GlobalErrorFallback = ({ error, resetErrorBoundary }) => {
  // ... Değişiklik yok ...
  return (
    <div
      role="alert"
      style={{ padding: "20px", textAlign: "center", margin: "auto" }}
    >
      <h2>Beklenmedik bir hata oluştu!</h2>
      <p>Uygulamanın bu bölümü maalesef çöktü.</p>
      {process.env.NODE_ENV === "development" && (
        <pre
          style={{ color: "red", textAlign: "left", whiteSpace: "pre-wrap" }}
        >
          {error.message}
        </pre>
      )}
      <button
        onClick={() => window.location.reload()}
        style={{ marginTop: "10px", padding: "10px 20px" }}
      >
        Sayfayı Yenile
      </button>
    </div>
  );
};

// ProtectedRoute component'iniz aynı kalıyor
const ProtectedRoute = ({ children }) => {
  const { token, loading } = useContext(AuthContext);
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Yükleniyor...
      </div>
    );
  }
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const ProtectedLayout = () => {
  const { user } = useContext(AuthContext);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [currentErrorMessage, setCurrentErrorMessage] = useState("");
  const [currentErrorDetails, setCurrentErrorDetails] = useState("");

  useEffect(() => {
    setErrorDisplayCallback((message, details) => {
      setCurrentErrorMessage(message);
      setCurrentErrorDetails(details);
      setIsErrorModalOpen(true);
    });
  }, []);

  const handleCloseErrorModal = () => {
    setIsErrorModalOpen(false);
    setCurrentErrorMessage("");
    setCurrentErrorDetails("");
  };

  const handleSendErrorEmail = async () => {
    try {
      const errorData = {
        subject: "Frontend Uygulamasında Hata Raporu",
        message: `Kullanıcıdan gelen hata:\n\nMesaj: ${currentErrorMessage}\nDetay: ${currentErrorDetails}\n\nKullanıcı ID: ${
          user?.id || "Bilinmiyor"
        }\nURL: ${window.location.href}`,
        userId: user?.id || null,
      };
      await sendErrorEmail(errorData);
      alert("Hata başarıyla raporlandı! Teşekkür ederiz.");
    } catch (emailError) {
      // console.error("E-posta gönderme hatası:", emailError);
      alert("Hata raporu gönderilirken bir sorun oluştu.");
    } finally {
      handleCloseErrorModal();
    }
  };

  return (
    <div
      className="app-container"
      style={{ display: "flex", minHeight: "100vh" }}
    >
      <Sidebar />
      <main
        className="main-content"
        style={{
          flex: 1,
          padding: "20px",
          backgroundColor: "#f4f6f8",
          overflowY: "auto",
        }}
      >
        <ErrorBoundary
          FallbackComponent={GlobalErrorFallback}
          onError={(error, info) => {}}
        >
          <Outlet />
        </ErrorBoundary>
      </main>
      <ErrorModal
        open={isErrorModalOpen}
        onClose={handleCloseErrorModal}
        errorMessage={currentErrorMessage}
        errorDetails={currentErrorDetails}
        onSendErrorEmail={handleSendErrorEmail}
      />
    </div>
  );
};

function App() {
  return (
    <Router>
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/forgot-password-verify"
              element={<ForgotPasswordVerify />}
            />
            <Route path="/forgot-password-reset" element={<ResetPassword />} />

            <Route
              element={
                <ProtectedRoute>
                  <ProtectedLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/uretim-donemi" element={<UretimDonemiPage />} />
              <Route path="/uretici-bilgileri" element={<UreticiBilgileri />} />
              <Route path="/kume-bilgileri" element={<KumeBilgileri />} />
              <Route
                path="/performans-raporlari"
                element={<PerformansRaporlari />}
              />
              <Route
                path="/muhasebe-raporlari"
                element={<MuhasebeRaporlari />}
              />
              <Route path="/sistem-uyarilari" element={<SistemUyarilari />} />
              <Route path="/sss" element={<SSS />} />
              <Route path="/hesap-bilgilerim" element={<HesapBilgilerim />} />
              <Route path="/destek-talepleri" element={<DestekTalepleri />} />
              <Route path="/kullanim-kilavuzu" element={<KullanimKilavuzu />} />
              <Route path="/lisans-kosullari" element={<LisansKosullari />} />
              <Route
                path="/veterinerlik-hizmetleri"
                element={<VeterinerlikHizmetleri />}
              />
              <Route path="/bakicilarim" element={<BakicilarimPage />} />
              <Route
                path="/kumes/:kumesId/donem/:donemId"
                element={<DonemDetayPage />}
              />
              <Route path="/kumes-karti" element={<KumesKartiPage />} />
              <Route path="/kumes-karti/:donemId" element={<KumesKartiPage />} />
            </Route>

            <Route path="/" element={<Navigate to="/login" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </SnackbarProvider>
    </Router>
  );
}

export default App;
